import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Card, CardBody, Input } from "reactstrap";
import SideMenuBar from "./SideMenuBar";
import { gAPI_Context_Detail } from "../../../../../../constants/APIs";
import { AxiosAPI } from "../../../../../../helpers/AxiosAPI";

function ViewPublishContent({ contextData = [], itemCountsData, publishContentUrl }) {
    const [search, setSearch] = useState("");
    const [fullTextSearch, setFullTextSearch] = useState("");
    const [selectedSource, setSelectedSource] = useState("All");
    const [selectedStatus, setSelectedStatus] = useState("All");
    const [expanded, setExpanded] = useState({});
    const [fullContent, setFullContent] = useState({});
    const [loadingContent, setLoadingContent] = useState({});

    useEffect(() => {
        const timer = setTimeout(() => {
            setFullTextSearch(search);
        }, 300);

        return () => clearTimeout(timer);
    }, [search]);

    const fetchAllFullContent = async () => {
        const idsToFetch = contextData
            .filter(item => !fullContent[item.id])
            .map(item => item.id);

        if (idsToFetch.length === 0) return;

        try {
            const promises = idsToFetch.map(id =>
                AxiosAPI({
                    method: "get",
                    url: `${gAPI_Context_Detail}/${id}`,
                })
                    .then(res => ({
                        id,
                        content: res?.data?.content || ""
                    }))
                    .catch(() => ({ id, content: "" }))
            );

            const results = await Promise.all(promises);

            const newContentMap = {};
            results.forEach(r => {
                newContentMap[r.id] = r.content;
            });

            setFullContent(prev => ({
                ...prev,
                ...newContentMap
            }));

        } catch (error) {
            console.error("Error fetching full content", error);
        }
    };

    useEffect(() => {
        if (fullTextSearch.trim() !== "") {
            fetchAllFullContent();
        }
    }, [fullTextSearch]);

    const filteredData = useMemo(() => {
        let data = [...contextData];
        data = data.filter(
            (item) => item?.content && item.content.trim().length > 0
        );

        if (selectedStatus !== "All") {
            data = data.filter((item) => item.status === selectedStatus);
        }
        if (selectedSource !== "All") {
            data = data.filter((item) => item.type === selectedSource);
        }

        if (fullTextSearch.trim() !== "") {
            const keyword = fullTextSearch.toLowerCase();

            data = data.filter((item) => {
                const normalContent = item?.content || "";
                const expandedContent = fullContent[item.id] || "";

                return (
                    normalContent.toLowerCase().includes(keyword) ||
                    expandedContent.toLowerCase().includes(keyword)
                );
            });
        }

        return data;
    }, [
        contextData,
        selectedSource,
        selectedStatus,
        fullTextSearch,
        fullContent
    ]);

    const groupedData = useMemo(() => {
        return filteredData.reduce((acc, item) => {
            if (!item?.type) return acc;
            if (!acc[item.type]) {
                acc[item.type] = [];
            }
            acc[item.type].push(item);
            return acc;
        }, {});
    }, [filteredData]);

    const toggleSection = (type) => {
        setExpanded((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const handleShowMore = async (id) => {
        try {
            setLoadingContent((prev) => ({
                ...prev,
                [id]: true,
            }));

            const response = await AxiosAPI({
                method: "get",
                url: `${gAPI_Context_Detail}/${id}`,
            });

            setFullContent((prev) => ({
                ...prev,
                [id]: response?.data?.content || "",
            }));

        } catch (error) {
            console.error("Error fetching full content", error);
        } finally {
            setLoadingContent((prev) => ({
                ...prev,
                [id]: false,
            }));
        }
    };

    const parseSections = (rawContent) => {
        if (!rawContent) return [];
        const normalized = rawContent.replace(/\r\n|\n|\r/g, "\n");
        const parts = normalized
            .split("==")
            .map((p) => p.trim())
            .filter(Boolean);

        const sections = [];
        for (let i = 0; i < parts.length; i += 2) {
            const title = parts[i]?.replace(/^=+/, "").trim();
            const content = (parts[i + 1] || "")
                .replace(/^=+/, "")
                .trim();
            sections.push({ title, content });
        }
        return sections;
    };
    const highlightText = (text, keyword) => {
        if (!keyword) return text;

        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(${escapedKeyword})`, "gi");

        return text.split(regex).map((part, index) =>
            regex.test(part) ? (
                <mark key={index} style={{ backgroundColor: "#ffe58a", padding: 0 }}>
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };
    const SectionContent = ({ sections, isHeading, keyword }) => (
        sections.map((section, i) => (
            <div key={i} className="mb-3 pl-2">
                {isHeading ? (
                    <h6 style={{ fontWeight: 600 }}>
                        {highlightText(section.title, keyword)}
                    </h6>
                ) : (
                    <p>{highlightText(section.title, keyword)}</p>
                )}

                {(section.content || "")
                    .split(/\r?\n/)
                    .map((line, idx) => (
                        <div key={idx}>
                            {highlightText(line, keyword)}
                        </div>
                    ))}
            </div>
        ))
    );

    return (
        <Row>
            <Col md="12" style={{ paddingRight: "300px" }}>
                <Row className="mb-4">
                    <Col md="12">
                        <Input
                            type="text"
                            placeholder="Search through all published content..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Col>
                </Row>

                {Object.keys(groupedData).map((type) => (
                    <Card key={type} className="mb-3">
                        <CardBody>
                            <Row
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleSection(type)}
                            >
                                <Col md="10">
                                    <h5>{type}</h5>
                                </Col>
                                <Col md="2" className="text-right">
                                    <span style={{ fontSize: 20, color: "#6c757d" }}>
                                        {expanded[type] ? "▾" : "▸"}
                                    </span>
                                </Col>
                            </Row>

                            {expanded[type] &&
                                groupedData[type].map((item) => {
                                    const contentToShow =
                                        fullContent[item.id] || item?.content;
                                    const isExpanded = !!fullContent[item.id];

                                    return (
                                        <div key={item.id} className="mt-3 pl-3">
                                            <SectionContent
                                                sections={parseSections(contentToShow)}
                                                isHeading={!(type === "Documents" || type === "Forms")}
                                                keyword={fullTextSearch}
                                            />
                                            {!isExpanded && (
                                                <div
                                                    style={{
                                                        color: "#007bff",
                                                        cursor: "pointer",
                                                        marginTop: "5px",
                                                    }}
                                                    onClick={() =>
                                                        handleShowMore(item.id)
                                                    }
                                                >
                                                    {loadingContent[item.id]
                                                        ? "Loading..."
                                                        : "Show More"}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </CardBody>
                    </Card>
                ))}

                {filteredData.length === 0 && (
                    <div className="text-center text-muted mt-5">
                        No content found
                    </div>
                )}
            </Col>
            <SideMenuBar
                selectedSource={selectedSource}
                // selectedStatus={selectedStatus}
                onSourceChange={setSelectedSource}
                // onStatusChange={setSelectedStatus}
                contextData={contextData}
                itemCountsData={itemCountsData}
                publishContentUrl={publishContentUrl}
            />
        </Row>
    );
}
export default ViewPublishContent;